import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import ConnectivityBanner from './components/ConnectivityBanner';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import LanguagePreferenceModal from './components/LanguagePreferenceModal';
import Home from './pages/Home';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import GenericLearnPage from './pages/learn/GenericLearnPage';
import ConversationLearn from './pages/conversation/Conversation';
import VocabularyPage from './pages/vocabulary/VocabularyPage';
import CommonFoundationsPage from './pages/common/CommonFoundationsPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import ContactPage from './pages/legal/ContactPage';
import CreditsPage from './pages/legal/CreditsPage';
import PracticePage from './pages/practice/PracticePage';
import ProfilePage from './pages/profile/ProfilePage';
import ProgressPage from './pages/progress/ProgressPage';
import LeaderboardPage from './pages/leaderboard/LeaderboardPage';
import FriendsPage from './pages/friends/FriendsPage';
import PlacementTestPage from './pages/placement/PlacementTestPage';
import ReferralPage from './pages/referral/ReferralPage';
import AudioSlideshowPage from './pages/audio/AudioSlideshowPage';
import GrammarPage from './pages/grammar/GrammarPage';
import GrammarLessonPage from './pages/grammar/GrammarLessonPage';
import CommonFoundationsLessonPage from './pages/common/CommonFoundationsLessonPage';
import AlphabetLessonPage from './pages/learn/AlphabetLessonPage';
import VocabularyLessonPage from './pages/vocabulary/VocabularyLessonPage';
import StoriesPage from './pages/stories/StoriesPage';
import WritingPage from './pages/writing/WritingPage';
import ExercisePage from './pages/exercise/ExercisePage';
import SpeakPage from './pages/speak/SpeakPage';
import DownloadAppsPage from './pages/download/DownloadAppsPage';
import EnglishStaticPage from './pages/static/english/EnglishStaticPage';
import SpanishStaticPage from './pages/static/spanish/SpanishStaticPage';
import FrenchStaticPage from './pages/static/french/FrenchStaticPage';
import GermanStaticPage from './pages/static/german/GermanStaticPage';
import ItalianStaticPage from './pages/static/italian/ItalianStaticPage';
import QuickSheetPage from './pages/quick-sheet/QuickSheetPage';
import PhrasebookPage from './pages/phrasebook/PhrasebookPage';
import PhrasebookLanguagePage from './pages/phrasebook/PhrasebookLanguagePage';
import PhrasebookPhrasePage from './pages/phrasebook/PhrasebookPhrasePage';
import TopWordsPage from './pages/top-words/TopWordsPage';
import AudioLecturesPage from './pages/audio-lectures/AudioLecturesPage';
import CurriculumPage from './pages/curriculum/CurriculumPage';
import CurriculumDayPage from './pages/curriculum/CurriculumDayPage';
import SkillPathPage from './pages/journeys/SkillPathPage';
import TopicLessonsPathPage from './pages/journeys/TopicLessonsPathPage';
import TopicLessonPage from './pages/journeys/TopicLessonPage';
import GamesPage from './pages/games/GamesPage';
import WordMatchGame from './pages/games/WordMatchGame';
import BalloonPopGame from './pages/games/BalloonPopGame';
import MemoryCardsGame from './pages/games/MemoryCardsGame';
import WordSearchGame from './pages/games/WordSearchGame';
import HangmanGame from './pages/games/HangmanGame';
import SpeedQuizGame from './pages/games/SpeedQuizGame';
import FruitSliceGame from './pages/games/FruitSliceGame';
import PronunciationGuidePage from './pages/common/PronunciationGuidePage';
import PronunciationGuideLessonPage from './pages/common/PronunciationGuideLessonPage';
import BookmarksPage from './pages/bookmarks/BookmarksPage';
import WeakWordsPage from './pages/practice/WeakWordsPage';
import SearchPage from './pages/search/SearchPage';
import BossBattleGame from './pages/games/BossBattleGame';
import ListeningChallengeGame from './pages/games/ListeningChallengeGame';
import SentenceBuilderGame from './pages/games/SentenceBuilderGame';
import { useAppConfig } from './hooks/useAppConfig';
import { filterLanguagesByConfig, useLanguages } from './hooks/useLanguages';

function AppShell() {
  const {
    isReady,
    languagePrefs,
    isLanguageSelectionOpen,
    setLanguagePreferences,
  } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const { languages, loading: languagesLoading } = useLanguages();
  const { pathname } = useLocation();
  const nativeLanguages = filterLanguagesByConfig(
    languages,
    config.nativeLanguageList,
  );
  const targetLanguages = filterLanguagesByConfig(
    languages,
    config.targetLanguageList,
  );

  useEffect(() => {
    if (!isReady || languagesLoading || !languages.length) return;
    const nativeAvailable = nativeLanguages.some(
      (language) => language.code === languagePrefs?.nativeLanguage,
    );
    const targetAvailable = targetLanguages.some(
      (language) =>
        language.code === languagePrefs?.targetLanguage &&
        language.code !== languagePrefs?.nativeLanguage,
    );
    if (nativeAvailable && targetAvailable) return;

    const nativeLanguage = nativeLanguages.some((language) => language.code === 'en')
      ? 'en'
      : nativeLanguages[0]?.code;
    const targetLanguage = targetLanguages.some(
      (language) => language.code === 'es' && language.code !== nativeLanguage,
    )
      ? 'es'
      : targetLanguages.find((language) => language.code !== nativeLanguage)?.code;
    if (nativeLanguage && targetLanguage) {
      setLanguagePreferences({ nativeLanguage, targetLanguage });
    }
  }, [
    isReady,
    languagePrefs,
    languages,
    languagesLoading,
    nativeLanguages,
    setLanguagePreferences,
    targetLanguages,
  ]);

  return (
    <div className="app-shell">
      <ConnectivityBanner />
      {isLanguageSelectionOpen && <LanguagePreferenceModal />}
      <Navigation />
      <main className="app-main">
        <ErrorBoundary resetKey={pathname}>
          <Routes>
            {/* Public — reachable with no session at all: the auth pages
                themselves, legal/informational pages, and SEO landing pages.
                Everything else requires at least a guest (anonymous) or real
                signed-in session — see ProtectedRoute. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/download" element={<DownloadAppsPage />} />
            <Route path="/static/english" element={<EnglishStaticPage />} />
            <Route path="/static/spanish" element={<SpanishStaticPage />} />
            <Route path="/static/french" element={<FrenchStaticPage />} />
            <Route path="/static/german" element={<GermanStaticPage />} />
            <Route path="/static/italian" element={<ItalianStaticPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Home />} />
              <Route path="/skill-path" element={<SkillPathPage />} />
              <Route path="/topic-lessons/:level" element={<TopicLessonsPathPage />} />
              <Route path="/topic-lessons/:level/:lessonId" element={<TopicLessonPage />} />
              <Route path="/learn/conversations" element={<ConversationLearn />} />
              <Route path="/learn/stories" element={<StoriesPage />} />
              <Route path="/learn/writing" element={<WritingPage />} />
              <Route path="/listen/:langCode" element={<AudioSlideshowPage />} />
              <Route path="/exercises" element={<ExercisePage />} />
              <Route path="/speak" element={<SpeakPage />} />
              <Route path="/learn/vocabulary" element={<VocabularyPage />} />
              <Route path="/learn/vocabulary/:type/:categoryId/:sectionId/lesson" element={<VocabularyLessonPage />} />
              <Route path="/learn/common-foundations" element={<CommonFoundationsPage />} />
              <Route path="/learn/common-foundations/lesson" element={<CommonFoundationsLessonPage />} />
              <Route path="/learn/pronunciation-guide" element={<PronunciationGuidePage />} />
              <Route path="/learn/pronunciation-guide/:chapterId/lesson" element={<PronunciationGuideLessonPage />} />
              <Route path="/learn/grammar" element={<GrammarPage />} />
              <Route path="/learn/grammar/:lessonId/lesson" element={<GrammarLessonPage />} />
              <Route path="/learn/:langCode/alphabet/lesson" element={<AlphabetLessonPage />} />
              <Route path="/learn/:langCode" element={<GenericLearnPage />} />
              <Route path="/quick-sheet" element={<QuickSheetPage />} />
              <Route path="/phrasebook" element={<PhrasebookPage />} />
              <Route path="/phrasebook/language/:code" element={<PhrasebookLanguagePage />} />
              <Route path="/phrasebook/phrase/:phraseKey" element={<PhrasebookPhrasePage />} />
              <Route path="/top-words" element={<TopWordsPage />} />
              <Route path="/audio-lectures" element={<AudioLecturesPage />} />
              <Route path="/curriculum/:level/day/:day" element={<CurriculumDayPage />} />
              <Route path="/curriculum/:level" element={<CurriculumPage />} />
              <Route path="/practice" element={<PracticePage />} />
              <Route path="/practice/weak-words" element={<WeakWordsPage />} />
              <Route path="/bookmarks" element={<BookmarksPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/word-match" element={<WordMatchGame />} />
              <Route path="/games/balloon-pop" element={<BalloonPopGame />} />
              <Route path="/games/memory-cards" element={<MemoryCardsGame />} />
              <Route path="/games/word-search" element={<WordSearchGame />} />
              <Route path="/games/hangman" element={<HangmanGame />} />
              <Route path="/games/speed-quiz" element={<SpeedQuizGame />} />
              <Route path="/games/fruit-slice" element={<FruitSliceGame />} />
              <Route path="/games/boss-battle" element={<BossBattleGame />} />
              <Route path="/games/listening-challenge" element={<ListeningChallengeGame />} />
              <Route path="/games/sentence-builder" element={<SentenceBuilderGame />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/progress" element={<ProgressPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/friends" element={<FriendsPage />} />
              <Route path="/placement-test" element={<PlacementTestPage />} />
              <Route path="/invite" element={<ReferralPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
